/**
 * Approval-flow editor.
 *
 * `settings/approvalFlow` is the trigger table as data — every step, its condition, its
 * resolver and its order. Editing it here changes the workflow with no deploy.
 *
 * The preview at the bottom runs the same declarative condition evaluator the server
 * uses (lib/conditions.js is a deliberate client mirror of the functions copy), so an
 * admin can see which steps a hypothetical matter would trigger before saving.
 */

import { useEffect, useMemo, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { PeopleMultiSelect } from '../../components/Field';
import { useCollection, useDoc } from '../../lib/useFirestore';
import { evaluateCondition } from '../../lib/conditions';
import { ALL_FIELDS, CLIENT_SOURCE_OPTIONS } from '../../lib/formSchema';
import { sortPeople } from '../../lib/sortPeople';
import { dedupePeople, personLabel } from '../../lib/people';
import {
  APP_NAME_MAX_LENGTH,
  normalizeAppName,
  preserveGeneralSettings,
  workflowBehaviorSettings,
} from '../../lib/generalSettings';
import ConditionBuilder from '../../components/ConditionBuilder';
import AdminTestMode from './AdminTestMode';
import GroupFlowEditor from './GroupFlowEditor';

/**
 * The four the firm names first, then everything else — the order people ask for them in, rather
 * than the order they happened to be built in.
 */
const RESOLVERS = [
  ['managing_partner', 'Managing Partner'],
  ['general_counsel', 'General Counsel'],
  ['practice_group_leader', 'Practice Group Leader'],
  ['responsible_partner', 'Responsible Partner (named on the matter)'],
  ['specific_user', 'Someone else'],
  ['team_leader', 'Team Leader'],
  ['intake', 'Intake (anyone with the Intake role)'],
  ['managing_partner_or_pgl', 'Managing Partner OR Practice Group Leader'],
  ['pgl_or_team_leader', 'Practice Group Leader OR Team Leader'],
];

/** What a cc entry can be. Mirrors approval/recipients.js — keep the two in step. */
const CC_KINDS = [
  ['managing_partner', 'Managing Partner'],
  ['general_counsel', 'General Counsel'],
  ['practice_group_leader', 'Practice Group Leader'],
  ['responsible_partner', 'Responsible Partner'],
  ['team_leader', 'Team leader'],
  ['user', 'Someone else'],
  ['email', 'Other email address'],
];

const STEP_KINDS = [
  ['approval', 'Approval — approve, reject, or request more information'],
  ['notify', 'Notify — a one-way, read-only email; nothing to decide'],
  ['data_collection', 'Data collection — the holder fills in fields via a link'],
];

// The same scalar field types Field Builder allows as repeating-group columns. A step
// that collects/notifies about data needs a real input or a plain display value, so
// fields Field.jsx renders as directory lookups, whole repeating groups, or file uploads
// are excluded here rather than offered and then breaking on the magic-link mini-form.
const COLLECT_FIELD_TYPES = new Set([
  'text', 'textarea', 'number', 'money', 'date', 'email', 'tel',
  'yesno', 'checkbox', 'select', 'radio', 'tags',
]);
const COLLECTABLE_FIELDS = ALL_FIELDS.filter((f) => COLLECT_FIELD_TYPES.has(f.type));

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';

export default function AdminApprovalFlow() {
  const { data: stored, loading } = useDoc('settings/approvalFlow');
  const { data: general, loading: loadingGeneral } = useDoc('settings/general');
  const roster = useCollection('roster');
  const practiceGroups = useCollection('practiceGroups');
  const { data: areasOfLawDoc } = useDoc('settings/areasOfLaw');
  const { run, busy, error, saved } = useCallable();
  const workflowSettings = useCallable();
  const seeder = useCallable();
  const [flow, setFlow] = useState(null);
  const [workflowEnabled, setWorkflowEnabled] = useState(null);
  const [consistencyReviewEnabled, setConsistencyReviewEnabled] = useState(null);
  const [workflowPilotUids, setWorkflowPilotUids] = useState(null);
  const [pageGateRequired, setPageGateRequired] = useState(null);
  const [originationCapPercent, setOriginationCapPercent] = useState(null);
  const [appName, setAppName] = useState(null);
  const [sharingEnabled, setSharingEnabled] = useState(null);
  const [submitLabel, setSubmitLabel] = useState(null);
  const [completionCcText, setCompletionCcText] = useState(null);
  const [seedAttempted, setSeedAttempted] = useState(false);
  const completionSettings = useCallable();

  const areasOfLawOptions = useMemo(
    () => (areasOfLawDoc?.items || []).filter((i) => i.isActive !== false),
    [areasOfLawDoc]
  );

  useEffect(() => {
    if (stored && !flow) setFlow(structuredClone(stored));
  }, [stored, flow]);

  useEffect(() => {
    if (!loadingGeneral && workflowEnabled === null) {
      // An absent setting intentionally means enabled, preserving established behavior.
      setWorkflowEnabled(general?.workflowEnabled !== false);
    }
  }, [general, loadingGeneral, workflowEnabled]);
  useEffect(() => {
    if (!loadingGeneral && consistencyReviewEnabled === null) {
      setConsistencyReviewEnabled(general?.consistencyReviewEnabled !== false);
    }
  }, [general, loadingGeneral, consistencyReviewEnabled]);
  useEffect(() => {
    if (!loadingGeneral && workflowPilotUids === null) {
      setWorkflowPilotUids(Array.isArray(general?.workflowPilotUids) ? general.workflowPilotUids : []);
    }
  }, [general, loadingGeneral, workflowPilotUids]);
  useEffect(() => {
    if (!loadingGeneral && pageGateRequired === null) {
      // Missing documents/settings retain the v1.8.2 default: the gate is on.
      setPageGateRequired(general?.pageGateDisabled !== true);
    }
  }, [general, loadingGeneral, pageGateRequired]);
  useEffect(() => {
    if (!loadingGeneral && originationCapPercent === null) {
      const cap = Number(general?.originationCapPercent);
      setOriginationCapPercent(Number.isFinite(cap) && cap >= 1 && cap <= 100 ? cap : 75);
    }
  }, [general, loadingGeneral, originationCapPercent]);
  useEffect(() => {
    if (!loadingGeneral && appName === null) {
      setAppName(normalizeAppName(general?.appName));
      // Both default ON / empty when unset, so an absent setting behaves as it always has.
      setSharingEnabled(general?.sharingEnabled !== false);
      setSubmitLabel(String(general?.submitButtonLabel || ''));
    }
  }, [general, loadingGeneral, appName]);
  useEffect(() => {
    if (!loadingGeneral && completionCcText === null) {
      setCompletionCcText((general?.matterCompletionCcEmails || []).join('\n'));
    }
  }, [general, loadingGeneral, completionCcText]);

  // `settings/approvalFlow` may not exist yet on a fresh production project (the
  // seed data in functions/scripts/seed.js only ever ran against the emulator).
  // Previously this left the page stuck on "Loading flow…" forever with no way to
  // create the doc from the UI. Seed defaults once (idempotent, additive-only) and
  // then let the live useDoc() snapshot pick up the newly-created doc normally.
  useEffect(() => {
    if (!loading && !stored && !seedAttempted) {
      setSeedAttempted(true);
      seeder.run('adminSeedDefaultSettings', {});
    }
  }, [loading, stored, seedAttempted, seeder]);

  if (loading || (!flow && !seedAttempted)) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading workflow…</p>;
  }
  if (!flow && seedAttempted) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {seeder.busy
          ? 'Setting up workflow defaults for the first time…'
          : seeder.error
            ? `Could not set up workflow defaults: ${seeder.error}`
            : 'Setting up workflow defaults…'}
      </p>
    );
  }

  const patchStep = (index, patch) =>
    setFlow({
      ...flow,
      steps: flow.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    });

  /**
   * One cc entry on one step. Changing the KIND drops the other kinds' fields, so switching from
   * "Someone else" to an address does not leave a stale uid behind for the server to resolve.
   */
  const patchCc = (stepIndex, ccIndex, patch) => {
    const step = flow.steps[stepIndex];
    const list = Array.isArray(step.cc) ? step.cc : [];
    patchStep(stepIndex, {
      cc: list.map((entry, i) => (i === ccIndex ? { ...('kind' in patch ? { kind: entry.kind } : entry), ...patch } : entry)),
    });
  };

  const save = () => run('adminUpdateSettings', { docId: 'approvalFlow', value: flow });
  const saveWorkflowSetting = () => {
    return workflowSettings.run('adminUpdateSettings', {
      docId: 'general',
      value: workflowBehaviorSettings(general, {
        workflowEnabled,
        consistencyReviewEnabled,
        workflowPilotUids: workflowPilotUids || [],
      }),
    });
  };
  const saveIntakeSettings = () => {
    // settings/general is shared by this and other admin pages. adminUpdateSettings
    // overwrites the document (merge: false), so preserve every existing field and
    // override only this section's two settings.
    return workflowSettings.run('adminUpdateSettings', {
      docId: 'general',
      value: preserveGeneralSettings(general, {
        pageGateDisabled: !pageGateRequired,
        originationCapPercent: Number(originationCapPercent),
        appName: normalizeAppName(appName),
        sharingEnabled: sharingEnabled !== false,
        submitButtonLabel: String(submitLabel || '').trim(),
      }),
    });
  };
  const saveCompletionSettings = () => {
    // Comma- or newline-separated, blanks skipped, only kept if it looks like an email.
    const emails = completionCcText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
    return completionSettings.run('adminUpdateSettings', {
      docId: 'general',
      value: preserveGeneralSettings(general, { matterCompletionCcEmails: emails }),
    });
  };

  const sorted = [...flow.steps].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save workflow
        </button>
        <button
          type="button"
          onClick={() => setFlow(structuredClone(stored))}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
        >
          Discard changes
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Workflow</h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          When disabled, new matters are submitted directly and the submitter receives the
          full matter-record PDF instead of entering approval routing. Matters already in
          progress are not changed.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={workflowEnabled !== false}
              disabled={loadingGeneral || workflowEnabled === null}
              onChange={(event) => setWorkflowEnabled(event.target.checked)}
            />
            Enable workflow
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={consistencyReviewEnabled !== false}
              disabled={loadingGeneral || consistencyReviewEnabled === null}
              onChange={(event) => setConsistencyReviewEnabled(event.target.checked)}
            />
            Enable Review for consistency
          </label>
          <button
            type="button"
            onClick={saveWorkflowSetting}
            disabled={
              workflowSettings.busy ||
              loadingGeneral ||
              workflowEnabled === null ||
              consistencyReviewEnabled === null ||
              workflowPilotUids === null
            }
            className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Save workflow setting
          </button>
          <CallStatus {...workflowSettings} />
        </div>

        {workflowEnabled === false && (
          <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
            <h3 className="text-sm font-medium text-amber-900 dark:text-amber-100">
              Pilot: run the real workflow for specific people
            </h3>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
              While the workflow is off firm-wide, matters submitted by these people still build
              the real approval chain — so it can be tested end to end without routing everyone
              else's submissions. Their approvers get <strong>real emails</strong>, so switch on
              Test mode below first if you want those redirected to a single address. Whether a
              matter used the workflow is recorded on the matter itself, so its record keeps its
              approval trail even after someone leaves this list. Saved with{' '}
              <strong>Save workflow setting</strong> above.
            </p>
            <div className="mt-2">
              <PeopleMultiSelect
                field={{ label: 'Pilot submitters', placeholder: 'Add a pilot submitter…' }}
                value={workflowPilotUids || []}
                onChange={setWorkflowPilotUids}
                disabled={loadingGeneral || workflowPilotUids === null}
                config={{ roster: dedupePeople(roster.data) }}
              />
            </div>
          </div>
        )}
      </section>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          Application and intake settings
        </h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          These firm-wide settings apply to the header and every new matter form. They do not
          change completion indicators or the requirement that all origination shares total 100%.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Application name
            <input
              type="text"
              maxLength={APP_NAME_MAX_LENGTH}
              className={`${field} mt-1 w-64`}
              value={appName ?? ''}
              disabled={loadingGeneral || appName === null}
              onChange={(event) => setAppName(event.target.value)}
              aria-describedby="application-name-help"
            />
            <span id="application-name-help" className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
              Shown in the header. Empty values use the default name.
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={pageGateRequired === true}
              disabled={loadingGeneral || pageGateRequired === null}
              onChange={(event) => setPageGateRequired(event.target.checked)}
            />
            Require all fields on a page before continuing
          </label>
          {/*
            Two presentation settings. Neither changes what a matter records or who may see it —
            they only control what the person filling in the form is offered and what the button
            says.
          */}
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={sharingEnabled === true}
              disabled={loadingGeneral || sharingEnabled === null}
              onChange={(event) => setSharingEnabled(event.target.checked)}
              aria-describedby="sharing-help"
            />
            Offer &ldquo;Shared with&rdquo; on a matter
          </label>
          <span id="sharing-help" className="w-full text-xs text-slate-500 dark:text-slate-400">
            Unticking this hides the sharing card from the intake form and the matter page. It does
            <strong> not</strong> remove access from anyone a matter is already shared with — they keep
            it until someone with the card visible removes them, or you ask for a separate clear-out.
          </span>
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Submit button text
            <input
              type="text"
              maxLength={40}
              className={`${field} mt-1 w-64`}
              value={submitLabel ?? ''}
              disabled={loadingGeneral || submitLabel === null}
              onChange={(event) => setSubmitLabel(event.target.value)}
              aria-describedby="submit-label-help"
              placeholder={workflowEnabled ? 'Submit for approval' : 'Send / Download PDF'}
            />
            <span id="submit-label-help" className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
              Leave empty for the default, which follows the approval-workflow setting:
              &ldquo;Submit for approval&rdquo; when it is on, &ldquo;Send / Download PDF&rdquo; when it
              is off. A value here replaces both.
            </span>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Maximum origination percentage per attorney
            <input
              type="number"
              min="1"
              max="100"
              className={`${field} mt-1 w-24`}
              value={originationCapPercent ?? ''}
              disabled={loadingGeneral || originationCapPercent === null}
              onChange={(event) => setOriginationCapPercent(event.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={saveIntakeSettings}
            disabled={
              workflowSettings.busy ||
              loadingGeneral ||
              pageGateRequired === null ||
              originationCapPercent === null ||
              appName === null
            }
            className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Save intake settings
          </button>
          <CallStatus {...workflowSettings} />
        </div>
      </section>

      <div className="space-y-3">
        {sorted.map((step) => {
          const index = flow.steps.indexOf(step);
          return (
            <div key={step.key} className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={step.enabled !== false}
                    onChange={(e) => patchStep(index, { enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <input
                  className={`${field} w-16`}
                  type="number"
                  value={step.order}
                  onChange={(e) => patchStep(index, { order: Number(e.target.value) })}
                  title="Order"
                />
                <input
                  className={`${field} flex-1`}
                  value={step.label}
                  onChange={(e) => patchStep(index, { label: e.target.value })}
                />
                {step.isStandard && (
                  <span className="rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-800 dark:text-firm-100">
                    standard chain
                  </span>
                )}
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-slate-600 dark:text-slate-300">
                  Kind
                  <select
                    className={`${field} mt-1`}
                    value={step.kind || 'approval'}
                    onChange={(e) => {
                      const kind = e.target.value;
                      // 'blocking' is only meaningful for data_collection; clear it going
                      // to/from another kind so a stale value can't linger unseen.
                      patchStep(index, { kind, ...(kind === 'data_collection' ? {} : { blocking: undefined }) });
                    }}
                  >
                    {STEP_KINDS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                {step.kind === 'data_collection' && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={step.blocking !== false}
                      onChange={(e) => patchStep(index, { blocking: e.target.checked })}
                    />
                    Hold up the rest of the chain until this is filled in
                  </label>
                )}

                {(step.kind === 'notify' || step.kind === 'data_collection') && (
                  <label className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
                    Fields {step.kind === 'notify' ? 'to send' : 'to collect'}
                    <select
                      multiple
                      className={`${field} mt-1 h-28`}
                      value={step.collectFields || []}
                      onChange={(e) =>
                        patchStep(index, {
                          collectFields: Array.from(e.target.selectedOptions, (o) => o.value),
                        })
                      }
                    >
                      {COLLECTABLE_FIELDS.map((f) => (
                        <option key={f.path} value={f.path}>
                          {f.sectionTitle} — {f.label}
                        </option>
                      ))}
                    </select>
                    <span className="mt-1 block text-slate-500 dark:text-slate-400">
                      Ctrl/Cmd-click to select more than one.
                    </span>
                  </label>
                )}

                <label className="text-xs text-slate-600 dark:text-slate-300">
                  {step.kind === 'notify' ? 'Send to' : step.kind === 'data_collection' ? 'Who fills this in' : 'Approver'}
                  <select
                    className={`${field} mt-1`}
                    value={step.resolver}
                    onChange={(e) => patchStep(index, { resolver: e.target.value })}
                  >
                    {RESOLVERS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                {step.resolver === 'specific_user' && (
                  <label className="text-xs text-slate-600 dark:text-slate-300">
                    Person
                    <select
                      className={`${field} mt-1`}
                      value={step.specificUid || ''}
                      onChange={(e) => patchStep(index, { specificUid: e.target.value })}
                    >
                      <option value="">Select…</option>
                      {sortPeople(dedupePeople(roster.data)).map((u) => (
                        <option key={u.id} value={u.id}>
                          {personLabel(u)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="text-xs text-slate-600 dark:text-slate-300">
                  When there are several eligible approvers
                  <select
                    className={`${field} mt-1`}
                    value={step.satisfyMode || 'any'}
                    onChange={(e) => patchStep(index, { satisfyMode: e.target.value })}
                  >
                    <option value="any">Any one of them may approve</option>
                    <option value="all">All of them must approve</option>
                  </select>
                </label>

                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={Boolean(step.parallel)}
                    onChange={(e) => patchStep(index, { parallel: e.target.checked })}
                  />
                  Runs in parallel with the adjacent parallel step
                </label>

                {/*
                  Copied, not asked. A cc receives the email and nothing else: the magic link is
                  minted for the approver alone, so nobody here can approve, reject, or hold up a
                  matter. That separation is why this is its own list rather than more approvers.
                */}
                <div className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
                  Also copy on this email
                  <div className="mt-1 space-y-2">
                    {(Array.isArray(step.cc) ? step.cc : []).map((entry, ccIndex) => (
                      <div key={ccIndex} className="flex flex-wrap items-center gap-2">
                        <select
                          className={`${field} w-auto`}
                          value={entry.kind || 'email'}
                          onChange={(e) => patchCc(index, ccIndex, { kind: e.target.value })}
                        >
                          {CC_KINDS.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>

                        {entry.kind === 'user' && (
                          <select
                            className={`${field} w-auto`}
                            value={entry.uid || ''}
                            onChange={(e) => patchCc(index, ccIndex, { uid: e.target.value })}
                          >
                            <option value="">Select…</option>
                            {sortPeople(dedupePeople(roster.data)).map((u) => (
                              <option key={u.id} value={u.id}>
                                {personLabel(u)}
                              </option>
                            ))}
                          </select>
                        )}

                        {entry.kind === 'email' && (
                          <input
                            className={`${field} w-auto`}
                            placeholder="records@aalrr.com"
                            value={entry.email || ''}
                            onChange={(e) => patchCc(index, ccIndex, { email: e.target.value })}
                          />
                        )}

                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline dark:text-red-400"
                          onClick={() =>
                            patchStep(index, { cc: (step.cc || []).filter((_, i) => i !== ccIndex) })
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="rounded border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                      onClick={() => patchStep(index, { cc: [...(step.cc || []), { kind: 'email', email: '' }] })}
                    >
                      + Add a copy
                    </button>
                  </div>
                  <span className="mt-1 block text-slate-500 dark:text-slate-400">
                    They receive the email only — they cannot approve, and an entry that matches
                    nobody is skipped rather than holding the matter up.
                  </span>
                </div>

                <label className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
                  Message shown to the approver
                  <textarea
                    rows={2}
                    className={`${field} mt-1`}
                    value={step.reasonTemplate || ''}
                    onChange={(e) => patchStep(index, { reasonTemplate: e.target.value })}
                  />
                </label>

                <label className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
                  Trigger condition
                  <div className="mt-1">
                    <ConditionBuilder
                      value={step.condition}
                      onChange={(condition) => patchStep(index, { condition })}
                      practiceGroups={practiceGroups.data.map((g) => ({ id: g.id, name: g.name || g.id }))}
                      areasOfLaw={areasOfLawOptions}
                    />
                  </div>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => {
          const maxOrder = flow.steps.reduce((max, s) => Math.max(max, Number(s.order) || 0), 0);
          const usedKeys = new Set(flow.steps.map((s) => s.key));
          let key = 'new_step';
          let n = 2;
          while (usedKeys.has(key)) {
            key = `new_step_${n}`;
            n += 1;
          }
          setFlow({
            ...flow,
            steps: [
              ...flow.steps,
              {
                key,
                type: key,
                enabled: true,
                order: maxOrder + 10,
                label: 'New step',
                isStandard: false,
                parallel: false,
                kind: 'approval',
                condition: true,
                resolver: 'specific_user',
                satisfyMode: 'all',
                reasonTemplate: '',
              },
            ],
          });
        }}
        className="rounded border border-dashed border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        + Add step
      </button>

      <FlowOptions flow={flow} setFlow={setFlow} roster={roster.data} />
      <GroupFlowEditor
        flow={flow}
        setFlow={setFlow}
        practiceGroups={practiceGroups.data}
        roster={roster.data}
      />

      <TriggerPreview flow={flow} practiceGroups={practiceGroups.data} />

      <div className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Matter-completion email</h2>
          <CallStatus {...completionSettings} />
        </div>
        <label className="mt-1 block text-xs text-slate-500 dark:text-slate-400" htmlFor="matter-completion-cc">
          Also send the completed-matter PDF to (comma-separated emails, optional)
        </label>
        <textarea
          id="matter-completion-cc"
          className={`${field} mt-2`}
          rows={3}
          placeholder="e.g. records@aalrr.com, docketing@aalrr.com"
          value={completionCcText ?? ''}
          onChange={(e) => setCompletionCcText(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          These addresses are cc’d only on the “matter fully approved” email (which includes the
          full-record PDF). They never replace the submitter as the primary recipient.
        </p>
        <button
          type="button"
          onClick={saveCompletionSettings}
          disabled={completionSettings.busy || completionCcText === null}
          className="mt-3 rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
        >
          Save completion email setting
        </button>
      </div>

      <AdminTestMode />
    </div>
  );
}

function FlowOptions({ flow, setFlow, roster }) {
  const reminder = flow.reminder || {};
  const patchReminder = (patch) => setFlow({ ...flow, reminder: { ...reminder, ...patch } });

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Flow options</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Managing Partner
          <select
            className={`${field} mt-1`}
            value={flow.managingPartnerUid || ''}
            onChange={(e) => setFlow({ ...flow, managingPartnerUid: e.target.value })}
          >
            <option value="">— resolve from roles —</option>
            {sortPeople(dedupePeople(roster)).map((u) => (
              <option key={u.id} value={u.id}>
                {personLabel(u)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          General Counsel
          <select
            className={`${field} mt-1`}
            value={flow.generalCounselUid || ''}
            onChange={(e) => setFlow({ ...flow, generalCounselUid: e.target.value })}
          >
            <option value="">— resolve from roles —</option>
            {sortPeople(dedupePeople(roster)).map((u) => (
              <option key={u.id} value={u.id}>
                {personLabel(u)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={Boolean(flow.deduplication?.enabled)}
            onChange={(e) =>
              setFlow({
                ...flow,
                deduplication: { ...flow.deduplication, enabled: e.target.checked },
              })
            }
          />
          <span>
            <strong>Combine consecutive steps with the same approver.</strong> Stops one
            person receiving two approval emails in a row for the same matter.
          </span>
        </label>

        <label className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={Boolean(flow.deduplication?.mergeAcrossStandardChain)}
            onChange={(e) =>
              setFlow({
                ...flow,
                deduplication: {
                  ...flow.deduplication,
                  mergeAcrossStandardChain: e.target.checked,
                },
              })
            }
          />
          <span>
            …including into the standard PGL / General Counsel chain. Off by default:
            folding a conditional review into the final sign-off makes the audit trail
            imply both happened at the same moment.
          </span>
        </label>

        <label className="text-xs text-slate-600 dark:text-slate-300">
          Remind after (business days)
          <input
            type="number"
            className={`${field} mt-1`}
            value={reminder.thresholdBusinessDays ?? 2}
            onChange={(e) => patchReminder({ thresholdBusinessDays: Number(e.target.value) })}
          />
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Then every (hours)
          <input
            type="number"
            className={`${field} mt-1`}
            value={reminder.cadenceHours ?? 24}
            onChange={(e) => patchReminder({ cadenceHours: Number(e.target.value) })}
          />
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Maximum reminders before escalation
          <input
            type="number"
            className={`${field} mt-1`}
            value={reminder.maxReminders ?? 3}
            onChange={(e) => patchReminder({ maxReminders: Number(e.target.value) })}
          />
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Escalate to
          <select
            className={`${field} mt-1`}
            value={reminder.escalationUid || ''}
            onChange={(e) => patchReminder({ escalationUid: e.target.value || null })}
          >
            <option value="">— the Managing Partner —</option>
            {sortPeople(dedupePeople(roster)).map((u) => (
              <option key={u.id} value={u.id}>
                {personLabel(u)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Magic-link lifetime (days)
          <input
            type="number"
            className={`${field} mt-1`}
            value={flow.tokenTtlDays ?? 14}
            onChange={(e) => setFlow({ ...flow, tokenTtlDays: Number(e.target.value) })}
          />
        </label>
      </div>
    </section>
  );
}

/** Which steps would a matter with these answers trigger? Same evaluator as the server. */
function TriggerPreview({ flow, practiceGroups = [] }) {
  const [probe, setProbe] = useState({
    clientSource: { source: 'marketing_efforts' },
    matterType: { isLitigation: false, isNonLitigation: true },
    litigation: {},
    nonLitigation: {},
    priorRepresentation: {},
    // isNewClient and practiceGroupId are probed explicitly because the General Counsel
    // gate branches on both; without them the preview would never show that step.
    header: { isNewClient: true, practiceGroupId: '', engagementLetter: { signedOnFile: true, waived: false } },
    representation: {},
  });

  const triggered = useMemo(
    () =>
      [...flow.steps]
        .filter((s) => s.enabled !== false)
        .sort((a, b) => a.order - b.order)
        .filter((s) => {
          try {
            return evaluateCondition(s.condition, probe);
          } catch {
            return true;
          }
        }),
    [flow, probe]
  );

  const set = (path, value) =>
    setProbe((prev) => {
      const next = structuredClone(prev);
      const keys = path.split('.');
      let cursor = next;
      keys.slice(0, -1).forEach((k) => {
        cursor[k] = cursor[k] || {};
        cursor = cursor[k];
      });
      cursor[keys.at(-1)] = value;
      return next;
    });

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Preview</h2>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Answer a few questions as a submitter would and see the resulting chain.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Client source
          <select
            className={`${field} mt-1`}
            value={probe.clientSource.source}
            onChange={(e) => set('clientSource.source', e.target.value)}
          >
            {CLIENT_SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Practice group
          <select
            className={`${field} mt-1`}
            value={probe.header.practiceGroupId || ''}
            onChange={(e) => set('header.practiceGroupId', e.target.value)}
          >
            <option value="">— none selected —</option>
            {practiceGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name || g.id}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={probe.header.isNewClient === true}
            onChange={(e) => set('header.isNewClient', e.target.checked)}
          />
          New client (unchecked = existing client)
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={probe.matterType.isLitigation}
            onChange={(e) => set('matterType.isLitigation', e.target.checked)}
          />
          Litigation matter
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={probe.litigation.handledBefore === false}
            onChange={(e) => set('litigation.handledBefore', e.target.checked ? false : true)}
          />
          Firm has NOT handled this type of litigation before
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={Boolean(probe.header.engagementLetter.waived)}
            onChange={(e) => set('header.engagementLetter.waived', e.target.checked)}
          />
          Engagement letter waived
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={probe.priorRepresentation.adverseToFormerClient === true}
            onChange={(e) => set('priorRepresentation.adverseToFormerClient', e.target.checked)}
          />
          Adverse to a former client
        </label>
      </div>

      <ol className="mt-4 space-y-1">
        {triggered.map((s, i) => (
          <li key={s.key} className="text-sm text-slate-800 dark:text-slate-100">
            {i + 1}. {s.label}{' '}
            <span className="text-xs text-slate-500 dark:text-slate-400">
              ({s.resolver}
              {s.isStandard ? ', standard' : ''})
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
        Preview evaluates conditions only; the server additionally resolves real approvers
        and de-duplicates adjacent steps.
      </p>
    </section>
  );
}
